/**
 * @file legacy-steps.ts
 * @description Таблицы перевода старых ступеней оформления в числа (phase19.16,
 * партия SB120 «свобода выбора», этап 0; site-builder.md SB120; проект полей
 * docs/phases/phase19.16-materials/uiux-sb120-fields.md §2 и §6).
 *
 * Принцип §6: после перевода на сайте не сдвигается ни один пиксель. Поэтому
 * каждое число здесь взято из того места, где ступень сегодня превращается в
 * CSS (ссылка «файл:строка» у каждого значения). Наследуемые «как у сайта» /
 * «Обычно» сюда не входят — они остаются наследуемыми; переводятся только
 * явные ступени. Ступени после перевода становятся подсказками-presets в
 * `NumberSliderField` (совпавшая подсвечена).
 *
 * Пользуются этапы 1–3 партии SB120 (миграция документов и пакетов «Как было»,
 * presets полей). Схему оформления этот файл не меняет. Таблицы ступеней темы
 * (плотность, заголовки, скругление, разрядка) с этапа 2 живут в legacy-v1.ts
 * (подъём темы v1 → v2) — прежние имена здесь ссылаются на них.
 */

import type { ElementSpaceStep, TileHeight } from "../site-section/legacy-v3.js";
import type { SpaceStep } from "../site-section/schema.js";
import {
  DENSITY_V1,
  densityFactor,
  EYEBROW_TRACKING_V1,
  HEADING_SCALE_V1,
  RADIUS_V1,
  type SiteDensityInput,
  type SiteEyebrowTrackingV1,
  type SiteHeadingScale,
  type SiteRadiusStepV1,
  type SiteSpacingDensityV1,
} from "./legacy-v1.js";

/**
 * Ступени отступов секции → px при плотности «Обычно».
 * Источник: shared/site-theme/css.ts:201 `SITE_SPACE_STEPS` (xs 8 · s 16 ·
 * m 32 · l 64 · xl 96) → `--site-space-*`; «0» — section.css:265/285
 * (`--sec-mt: 0px`) в apps/web/src/components/site/section/section.css.
 */
export const SPACE_STEP_PX: Readonly<Record<SpaceStep, number>> = {
  "0": 0,
  xs: 8,
  s: 16,
  m: 32,
  l: 64,
  xl: 96,
};

/**
 * Множитель плотности отступов. Источник: shared/site-theme/css.ts:194–198
 * `SITE_SPACE_SCALE` (compact 0.8 · normal 1 · spacious 1.25) →
 * `--site-space-scale`. С SB120 этапа 2 плотность темы — число; таблица
 * остаётся для строк v1 (v2-секции, старые документы) — это `DENSITY_V1`.
 */
export const DENSITY_FACTOR: Readonly<Record<SiteSpacingDensityV1, number>> = DENSITY_V1;

/**
 * Отступ ступени в px при плотности магазина — ровно как CSS-переменная
 * `--site-space-<ступень>` (css.ts:299–302 округляет до 0,1 px:
 * 0.8 × 8 = 6.4). «M» при «Просторно» = 32 × 1,25 = 40 px (§6).
 * На телефоне витрина дополнительно умножает на 0,6 (apps/web
 * globals.css:96 `--site-space-mobile`) — это свойство витрины, не числа.
 * Плотность — множитель темы v2 или строка v1 (`densityFactor`).
 */
export function spaceStepPx(step: SpaceStep, density: SiteDensityInput): number {
  return Math.round(SPACE_STEP_PX[step] * densityFactor(density) * 10) / 10;
}

/**
 * Ступени отступов элемента (сверху/снизу) → px при «Обычно». Та же шкала без
 * «XL»: apps/web/src/components/site/section/SectionElementV2.tsx:56–59
 * `elementStep` даёт `0px` или `var(--site-gap-<ступень>)`, а `--site-gap-*`
 * — apps/web/src/app/globals.css:97–100 (8 / 16 / 32 / 64 px × плотность).
 */
export const ELEMENT_SPACE_PX: Readonly<Record<ElementSpaceStep, number>> = {
  "0": 0,
  xs: 8,
  s: 16,
  m: 32,
  l: 64,
};

/**
 * Отступ элемента в px при плотности магазина (та же формула, что у секции).
 * Вход перевода v3 → v4 (upgrade-v4.ts): «0» → 0, остальные × плотность.
 */
export function elementSpacePx(step: ElementSpaceStep, density: SiteDensityInput): number {
  return Math.round(ELEMENT_SPACE_PX[step] * densityFactor(density) * 10) / 10;
}

/**
 * «Размер заголовков» → множитель кегля H1–H3. Источник:
 * shared/site-theme/css.ts:165–169 `HEADING_SCALE_FACTORS`
 * (s 0.875 · m 1 · l 1.125) → `--site-heading-scale`.
 */
export const HEADING_SCALE_FACTOR: Readonly<Record<SiteHeadingScale, number>> = HEADING_SCALE_V1;

/**
 * Скругление «нет / мягкое / крупное» → px карточек и фото (`card`,
 * `--site-radius`) и полей и бейджей (`field`, `--site-radius-control`).
 * Источник: shared/site-theme/css.ts:179–183 `RADIUS`
 * (none 0/0 · soft 8px/6px · large 20px/12px).
 */
export const RADIUS_PX: Readonly<Record<SiteRadiusStepV1, { readonly card: number; readonly field: number }>> =
  RADIUS_V1;

/**
 * Разрядка надзаголовков → em. Источник: shared/site-theme/css.ts:158–162
 * `TRACKING` (normal 0 · wide 0.08em · ultra 0.2em) → `--site-eyebrow-tracking`.
 */
export const TRACKING_EM: Readonly<Record<SiteEyebrowTrackingV1, number>> = EYEBROW_TRACKING_V1;

/**
 * Высота плитки (плитки и категории, SB24) → пропорция ширина ÷ высота.
 * Источник: shared/site-section/legacy-v3.ts `TILE_HEIGHT_RATIOS` (до v4 — schema.ts)
 * (low «3 / 2» · normal «1 / 1» · high «3 / 4») — CSS `aspect-ratio`.
 * Ключи — значения схемы (low/normal/high), подписи 3:2 / 1:1 / 3:4.
 */
export const TILE_ASPECT: Readonly<Record<TileHeight, number>> = {
  low: 1.5,
  normal: 1,
  high: 0.75,
};

// ── SB120 этап 1: оформление секции v2 → v3 (db-design-sb120-stage1 §2) ──

/**
 * «Наезд на блок выше» и «Выход фото за край» v2 (none / s / m) → ступень
 * шкалы. Источник: apps/web/src/components/site/section/section.css:111–125
 * (`--site-gap-l` / `--site-gap-xl`, т. е. `--site-space-l/xl` × плотность).
 * В px — `spaceStepPx(OVERLAP_STEP[v], плотность)`: 0 / 64 / 96 × плотность.
 */
export const OVERLAP_STEP: Readonly<Record<"none" | "s" | "m", SpaceStep>> = {
  none: "0",
  s: "l",
  m: "xl",
};

/**
 * Расстояния колонок v2 (`gap`, `elementGap`: s / m / l) → ступень шкалы.
 * Источник: SectionColumns.tsx `GAP` → `--site-gap-s/m/l` (16 / 32 / 64 ×
 * плотность). «M» — умолчание v2 и «как у сайта» v3 (нет ключа).
 */
export const GAP_STEP: Readonly<Record<"s" | "m" | "l", SpaceStep>> = { s: "s", m: "m", l: "l" };

/**
 * Рисунки секции v2 (size s / m / l) → ширина px на ПК и на телефоне.
 * Источник: apps/web/src/components/site/section/DecorLayer.tsx:42–46
 * (`w-16 md:w-24` · `w-24 md:w-40` · `w-36 md:w-64`); от плотности не зависит.
 */
export const DECOR_SIZE_PX: Readonly<Record<"s" | "m" | "l", { desktop: number; phone: number }>> = {
  s: { desktop: 96, phone: 64 },
  m: { desktop: 160, phone: 96 },
  l: { desktop: 256, phone: 144 },
};

/**
 * Точка 3×3 v2 (1 — левый верхний угол, по строкам) → место x / y в %.
 * Источник: DecorLayer.tsx:17–27 `ANCHOR` (left-0 / left-1/2 / right-0 ×
 * top-0 / top-1/2 / bottom-0) — то же место после перевода.
 */
export function anchorToPoint(anchor: number): { x: number; y: number } {
  const n = Math.min(9, Math.max(1, Math.trunc(anchor))) - 1;
  const steps = [0, 50, 100] as const;
  return { x: steps[n % 3]!, y: steps[Math.floor(n / 3)]! };
}

/**
 * Ширина блока v2 → v3: full — «Во всю» (отдельный режим), grid — «по сетке»
 * (нет ключа, 1280), narrow — 768. Источник:
 * apps/web/src/components/site/section/SectionV2.tsx:34–38 `CONTAINER`
 * (max-w-[1280px] / max-w-[768px]).
 */
export const CONTAINER_V3: Readonly<Record<"full" | "grid" | "narrow", "full" | number | undefined>> = {
  full: "full",
  grid: undefined,
  narrow: 768,
};
